// FILE: panelThreadSubscriptionStore.ts
// Purpose: Track threads mounted inside a panel so the WebSocket reconciler keeps streaming them
//   while the route sits on a different thread.
// Layer: UI state store
// Exports: the store, the mount/unmount hook, and the pure subscribed-id resolver.

import type { ThreadId } from "@taskin-code/contracts";
import { useEffect, useMemo } from "react";
import { create } from "zustand";

// Detail events for a thread only reach this client while it holds a subscription for that thread
// (see the scoped fan-out in wsServer). The route and the split view used to be the only things
// that could ask for one, so a panel showing another thread's transcript would render an empty
// timeline: the shell snapshot carries message roles and timestamps, never text.
interface PanelThreadSubscriptionStore {
  // Keyed by panel rather than counted: two panels can show the same thread, and closing one must
  // not cancel the other's subscription. Re-mounting the same panel is idempotent.
  threadIdByPanelId: Record<string, ThreadId | undefined>;
  mountPanelThread: (panelId: string, threadId: ThreadId) => void;
  unmountPanelThread: (panelId: string) => void;
}

export const usePanelThreadSubscriptionStore = create<PanelThreadSubscriptionStore>()((set) => ({
  threadIdByPanelId: {},
  mountPanelThread: (panelId, threadId) =>
    set((state) => {
      if (state.threadIdByPanelId[panelId] === threadId) {
        return state;
      }
      return { threadIdByPanelId: { ...state.threadIdByPanelId, [panelId]: threadId } };
    }),
  unmountPanelThread: (panelId) =>
    set((state) => {
      if (!Object.hasOwn(state.threadIdByPanelId, panelId)) {
        return state;
      }
      const threadIdByPanelId = { ...state.threadIdByPanelId };
      delete threadIdByPanelId[panelId];
      return { threadIdByPanelId };
    }),
}));

// Route/split-view ids come first so the thread the user is actually looking at is subscribed
// before any panel, which matters when the reconciler is applied in order.
export function resolveSubscribedThreadIds(input: {
  routeThreadIds: readonly ThreadId[];
  panelThreadIds: readonly ThreadId[];
}): ThreadId[] {
  return [...new Set([...input.routeThreadIds, ...input.panelThreadIds])];
}

/**
 * Keeps `threadId` subscribed for as long as the panel is mounted with it. Unmounting matters as
 * much as mounting: without it the server keeps pushing that thread's deltas indefinitely.
 */
export function usePanelThreadSubscription(panelId: string, threadId: ThreadId | null): void {
  const mountPanelThread = usePanelThreadSubscriptionStore((store) => store.mountPanelThread);
  const unmountPanelThread = usePanelThreadSubscriptionStore((store) => store.unmountPanelThread);

  useEffect(() => {
    if (!threadId) {
      unmountPanelThread(panelId);
      return;
    }
    mountPanelThread(panelId, threadId);
    return () => {
      unmountPanelThread(panelId);
    };
  }, [mountPanelThread, panelId, threadId, unmountPanelThread]);
}

export function useMountedPanelThreadIds(): ThreadId[] {
  const threadIdByPanelId = usePanelThreadSubscriptionStore((store) => store.threadIdByPanelId);
  return useMemo(
    () =>
      Object.values(threadIdByPanelId).filter(
        (threadId): threadId is ThreadId => threadId !== undefined,
      ),
    [threadIdByPanelId],
  );
}
